/**
 * Team Settings → Video (FR-TENANT-05, FR-VIDEO-03/04/05, AC-VIDEO-004).
 *
 *   1. **Provider selection.** The dropdown's option text carries the
 *      Fanatic-visibility implication ("Hudl — Coaches only"), and a badge
 *      legend repeats it visually, so a Team Admin cannot pick a provider
 *      without seeing who will be able to watch. Switching changes who sees
 *      video, so it goes through a consequence-stating confirmation.
 *   2. **Tenant-wide Hudl embed URL** — the fallback for events with no embed
 *      of their own.
 *   3. **Per-event embed configuration** — form + list, in their own files.
 *
 * `fanatic_visible` is rendered, never edited: it is declared by the
 * server-side adapter, and the service rejects an attempt to set it per tenant.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTenantSlug } from '../../app/tenant-slug';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DeskSection } from '../../components/desk-section';
import { TierLimitCta, readTierLimitDetail } from '../../components/tier-limit-cta';
import { ApiError } from '../../lib/api-error';
import type { VideoProviderCapability, VideoProviderName } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';

import { EventEmbedForm } from './event-embed-form';
import { EventEmbedTable } from './event-embed-table';
import { ProviderBadge } from './provider-badge';
import type { VideoSettingsController } from './use-video-settings';

const PROVIDER_HINT =
  "Select your team's video provider. Fan-visible providers let spectators and fans watch live " +
  'video; coaches-only providers are restricted to coaching staff and stat operators.';

const MAX_EMBED_URL_LENGTH = 2000;

/** Load-failure copy for the Video tab (kept together for translation). */
export const VIDEO_LOAD_FAILED_MESSAGE = 'Video settings could not be loaded.';
export const VIDEO_TIER_LIMIT_MESSAGE =
  'Video is not available on your current plan. Upgrade to connect a video provider and attach per-event embeds.';
export const VIDEO_ROUTE_MISSING_MESSAGE =
  'Video settings are not reachable right now: the API is not routing video requests. This is a deployment problem on our side, not something in your configuration — retry in a moment or contact support if it persists.';

function switchConsequence(next: VideoProviderCapability | undefined, label: string): string {
  if (next === undefined || !next.is_adapter) {
    return 'Video will be removed from Live Video and Game Summary for everyone on this team. Saved embed URLs are kept and reappear if you re-enable a provider.';
  }
  return next.fanatic_visible
    ? `Fans, parents and players will be able to watch ${label} video on Live Video and Game Summary.`
    : `${label} video will be visible to coaching staff and stat operators only. Fans, parents and players will no longer see the video slot.`;
}

export function VideoProviderSettings({
  controller,
}: {
  controller: VideoSettingsController;
}): JSX.Element {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const tenantSlug = useTenantSlug();
  const [pendingProvider, setPendingProvider] = useState<VideoProviderName | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [hudlDraft, setHudlDraft] = useState<string | null>(null);
  const [hudlError, setHudlError] = useState<string | null>(null);
  const [isSavingHudl, setIsSavingHudl] = useState(false);

  const { settings, isLoading, error, loadError, reload } = controller;

  if (settings === null) {
    if (isLoading) {
      return (
        <p role="status" className="muted">
          Loading video settings…
        </p>
      );
    }

    // 402 TIER_LIMIT_EXCEEDED — video is not included in this plan. An
    // action-oriented upgrade prompt, never a generic "try again".
    const tierLimit = readTierLimitDetail(loadError);
    if (tierLimit !== null) {
      return (
        <TierLimitCta
          limit={tierLimit.limitKey}
          featureDescription="Video"
          message={tierLimit.message ?? VIDEO_TIER_LIMIT_MESSAGE}
          currentPlan={tierLimit.currentPlan}
          minimumPlan={tierLimit.minimumPlan}
          onUpgrade={() =>
            navigate(
              `/app/${tenantSlug ?? ''}/${APP_VIEWS.billing}?tab=plan&limit=live_video_embed`
            )
          }
        />
      );
    }

    // 404 ROUTE_NOT_FOUND — the gateway has no rule for /video/v1/*. That is a
    // deployment fault, not something the Team Admin can fix by retrying, so
    // say so plainly instead of the raw "No API route matches this request".
    const isRouteMissing = loadError?.status === 404;
    return (
      <div className="banner banner--error" role="alert" data-testid="video-settings-error">
        <p>{isRouteMissing ? VIDEO_ROUTE_MISSING_MESSAGE : (error ?? VIDEO_LOAD_FAILED_MESSAGE)}</p>
        {isRouteMissing ? (
          <p className="muted">
            Technical detail: <code>{error}</code>
          </p>
        ) : null}
        <button type="button" className="btn btn--secondary btn--sm" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  const providers = settings.providers;
  const current = providers.find((entry) => entry.provider_name === settings.video_provider);
  const hudlValue = hudlDraft ?? settings.hudl_embed_url ?? '';

  async function commitProvider(provider: VideoProviderName): Promise<void> {
    setDialogError(null);
    try {
      const updated = await controller.saveSettings({ video_provider: provider });
      setPendingProvider(null);
      showToast(`Video provider updated to ${updated.provider_label ?? 'no video'}.`, 'success');
    } catch (caught) {
      setDialogError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The video provider could not be updated. Please try again.'
      );
    }
  }

  async function saveHudlUrl(): Promise<void> {
    const trimmed = hudlValue.trim();
    if (trimmed !== '' && !trimmed.startsWith('https://')) {
      setHudlError('The embed URL must start with https://.');
      return;
    }
    setHudlError(null);
    setIsSavingHudl(true);
    try {
      await controller.saveSettings({ hudl_embed_url: trimmed === '' ? null : trimmed });
      setHudlDraft(null);
      showToast('Hudl embed URL saved.', 'success');
    } catch (caught) {
      setHudlError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The Hudl embed URL could not be saved. Please try again.'
      );
    } finally {
      setIsSavingHudl(false);
    }
  }

  return (
    <div className="team-settings__panel">
      <div className="desk-grid team-settings__video-grid">
        <DeskSection title="Live video" description={PROVIDER_HINT}>
          <div className="field">
            <label className="field__label" htmlFor="video-provider">
              Video provider
            </label>
            <select
              id="video-provider"
              className="field__control"
              value={settings.video_provider}
              onChange={(event) => setPendingProvider(event.target.value as VideoProviderName)}
            >
              {providers.map((provider) => (
                <option key={provider.provider_name} value={provider.provider_name}>
                  {`${provider.label} — ${provider.description}`}
                </option>
              ))}
            </select>
            <span className="field__hint">
              Fan visibility is set by the provider, not by your team — it cannot be changed here.
            </span>
          </div>

          <ul className="team-settings__legend">
            {providers.map((provider) => (
              <li key={provider.provider_name} className="team-settings__legend-item">
                <span className="team-settings__legend-label">{provider.label}</span>
                <ProviderBadge provider={provider} />
                {provider.provider_name === settings.video_provider ? (
                  <span className="badge badge--current">Current</span>
                ) : null}
              </li>
            ))}
          </ul>
        </DeskSection>

        {settings.video_provider === 'hudl' ? (
          <DeskSection
            title="Team-wide Hudl embed"
            description="The full Hudl embed URL from your Hudl account. Used for any event with no embed of its own, and shown to every team member with access to live video."
          >
            <div className={`field${hudlError === null ? '' : ' field--invalid'}`}>
              <label className="field__label" htmlFor="hudl-embed-url">
                Hudl embed URL (optional)
              </label>
              <input
                id="hudl-embed-url"
                className="field__control"
                type="url"
                inputMode="url"
                maxLength={MAX_EMBED_URL_LENGTH}
                placeholder="https://www.hudl.com/embed/…"
                value={hudlValue}
                onChange={(event) => setHudlDraft(event.target.value)}
                aria-invalid={hudlError === null ? undefined : true}
                aria-describedby={hudlError === null ? undefined : 'hudl-embed-url-error'}
              />
              {hudlError === null ? null : (
                <span className="field__error" id="hudl-embed-url-error" role="alert">
                  {hudlError}
                </span>
              )}
            </div>
            <div className="desk-form__actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void saveHudlUrl()}
                disabled={isSavingHudl}
              >
                {isSavingHudl ? 'Saving…' : 'Save embed URL'}
              </button>
              {hudlDraft === null ? null : (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    setHudlDraft(null);
                    setHudlError(null);
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          </DeskSection>
        ) : null}
      </div>

      {current?.is_adapter === true ? (
        <EventEmbedForm controller={controller} provider={current} />
      ) : null}

      <EventEmbedTable controller={controller} providers={providers} />

      {pendingProvider === null ? null : (
        <ConfirmDialog
          title="Change video provider?"
          consequence={switchConsequence(
            providers.find((entry) => entry.provider_name === pendingProvider),
            providers.find((entry) => entry.provider_name === pendingProvider)?.label ??
              pendingProvider
          )}
          confirmLabel="Change provider"
          error={dialogError}
          onConfirm={() => void commitProvider(pendingProvider)}
          onCancel={() => {
            setPendingProvider(null);
            setDialogError(null);
          }}
        />
      )}
    </div>
  );
}
